import {
  Contact,
  ContactSetting,
  DocumentShareForm,
  GetContactSettingsResponseData,
  RequestContactSettingFieldValue,
  SiteShareForm
} from '@noco/http-client/lib/noco'
import { MuiIconVariant } from 'src/components/atoms'
import { DraggableData } from 'src/components/molecules'
import { TableColumn } from 'src/components/organisms'
import { ContactCompanyCell, NameCell, ActivityStatusCell } from 'src/components/organisms/Table/TableParts'
import { nonNullableFilter } from 'src/fixtures/utils/types'
import { DataTypeProvider } from '@devexpress/dx-react-grid'

export type ContactSettingValueMap = { [key in string]: { [fieldId: string]: any } }
export const initializeContactSettingValueMap = (settings: ContactSetting[], isEu = false): ContactSettingValueMap => {
  const initalSettingValueMap: ContactSettingValueMap = {}
  settings.forEach(s => {
    initalSettingValueMap[s.id!] = {}
    s.contactSettingFields?.forEach(item => {
      if (isEu && item.isHideOnEu) return
      if (!isEu && item.isHideOnUser) return
      initalSettingValueMap[s.id!][item.id!] = ''
    })
  })

  return initalSettingValueMap
}

export const convertFromContactSettingValueMap = (settingValueMap: ContactSettingValueMap) => {
  const contactSettingFieldValues: {
    contactSettingId: string
    contactSettingFieldId: string
    values?: { value: string }[]
  }[] = []
  Object.keys(settingValueMap).forEach(settingId => {
    const filedMap = settingValueMap[settingId]

    Object.keys(filedMap).forEach(fieldId => {
      // v は checkbox の場合のみ string[] / 他の場合は string
      const v: string[] | string = filedMap[fieldId]

      contactSettingFieldValues.push({
        contactSettingId: settingId,
        contactSettingFieldId: fieldId,
        values: Array.isArray(v) ? v.map(item => ({ value: item })) : [{ value: v }]
      })
    })
  })
  return contactSettingFieldValues
}

type ShareFormLike = Pick<DocumentShareForm | SiteShareForm, 'contactSetting' | 'isRequired'>

export const hasEmailSettingInShareForms = (forms?: ShareFormLike[]): boolean =>
  !!forms?.some(item => item.contactSetting?.settingType === 'email')

export const extractEmailFromSettingValueMap = (
  settingValueMap: ContactSettingValueMap,
  forms?: ShareFormLike[]
): string | undefined => {
  const emailForm = forms?.find(item => item.contactSetting?.settingType === 'email')
  const settingId = emailForm?.contactSetting?.id
  const fieldId = emailForm?.contactSetting?.contactSettingFields?.[0]?.id
  if (!settingId || !fieldId) return undefined
  const value = settingValueMap[settingId]?.[fieldId]
  return typeof value === 'string' && value ? value : undefined
}

// convert 済の RequestContactSettingFieldValue[] からメール値を抽出する
export const extractEmailFromRequestValues = (
  requestValues: RequestContactSettingFieldValue[] | undefined,
  forms?: ShareFormLike[]
): string | undefined => {
  if (!requestValues) return undefined
  const emailForm = forms?.find(item => item.contactSetting?.settingType === 'email')
  const settingId = emailForm?.contactSetting?.id
  if (!settingId) return undefined
  const target = requestValues.find(v => v.contactSettingId === settingId)
  const value = target?.values?.[0]?.value
  return typeof value === 'string' && value ? value : undefined
}

export const convertCompanyDataFromContact = (contact: Contact) => {
  const targetContactSetting = contact?.contactSettings?.find(item => item.settingType === 'company')
  const contactSettingId = targetContactSetting?.id
  const contactSettingFieldId = targetContactSetting?.contactSettingFields?.[0].id
  return { contactSettingId, contactSettingFieldId }
}

export const createDraggableSettingList = (
  data: GetContactSettingsResponseData,
  options?: { hideEdit?: boolean }
): DraggableData[] => {
  const activeData: DraggableData[] =
    data.activeContactSettings?.map(item => {
      // MEMO: snuffy isInputRequired は contactSettingFields 下を参照しつつ item.isInputRequiredOnEu || item.isInputRequired
      const isInputRequired = item?.contactSettingFields?.every(
        item => item.isInputRequiredOnEu || item.isInputRequired
      )
      return {
        id: item.id!,
        label: item.name!,
        icon: item.icon as MuiIconVariant,
        sort: item.sort || 0,
        description:
          item.kind === 'multiple_field' ? item?.contactSettingFields?.map(item => item.name).join('、') : undefined,
        isSelected: true,
        switchDisabled: isInputRequired,
        switchValue: isInputRequired,
        disabled: isInputRequired,
        isListType: item.contactSettingFields?.[0].itemType === 'list',
        canEdit: item.canEdit!,
        hideEdit: options?.hideEdit
      }
    }) || []

  const inactiveData =
    data.inactiveContactSettings?.map(item => ({
      id: item.id!,
      label: item.name!,
      icon: item.icon as MuiIconVariant,
      sort: item.sort || 0,
      isSelected: false,
      description:
        item.kind === 'multiple_field' ? item?.contactSettingFields?.map(item => item.name).join(' ,') : undefined,
      isListType: item.contactSettingFields?.[0].itemType === 'list',
      canEdit: item.canEdit!,
      hideEdit: options?.hideEdit
    })) || []

  return activeData.concat(inactiveData)
}

export const convertContactTableColumns = (activeContactSettings: ContactSetting[]) => {
  const priority = ['名前', '最新のアクティビティ', '名前（ふりがな）', '会社名']
  const columns =
    activeContactSettings?.map(item => {
      if (item?.name === '名前') {
        return {
          name: item.id!,
          width: 132,
          title: '名前',
          fixed: true
        }
      }
      if (item?.name === '会社名') {
        return {
          name: 'company',
          width: 132,
          title: '会社名',
          render: ContactCompanyCell
        }
      }
      if (item.name === '担当者') {
        return {
          name: item.id!,
          width: 132,
          title: '担当者',
          render: NameCell
        }
      }
      if (item.name === '最新のアクティビティ') {
        return {
          name: item.id!,
          width: 132,
          title: '最新のアクティビティ',
          render: ({ value }: DataTypeProvider.ValueFormatterProps) =>
            value?.activityStatus ? ActivityStatusCell({ value: value.activityStatus }) : null
        }
      }
      return {
        name: item.id!,
        width: 120,
        title: item?.name
      }
    }) || []

  // TODO: sort 一回で全て済ませる
  const priorityColumns =
    columns.length > 0
      ? priority
          .map(label => {
            return columns.find(item => item?.title === label) as TableColumn | undefined
          })
          .concat(columns.filter(b => !priority.includes(b?.title!)))
          .filter(nonNullableFilter)
      : []

  return priorityColumns
}
